import React, { useEffect, useRef, useState, ReactNode } from 'react'
import { OmButton, OmDrawer, OmPopover } from '@owma/kit'
import { useTranslation } from 'react-i18next'
import styles from './style.module.scss'

import AccountInfoView from 'pages/calendar/components/AccountInfoView'
import DayBox from 'pages/calendar/components/DayBox'
import MobileMonthBox from 'pages/calendar/components/MobileMonthBox'
import WeekBox from 'pages/calendar/components/WeekBox'
import CalendarDatePicker from 'pages/todo/components/DatePicker'
import { DatePickerType } from 'pages/todo/components/DatePicker/DatePickerType'
import SearchPortal from 'pages/main/components/SearchPortal'
import AgendaBox from 'pages/calendar/components/AgendaBox'

import * as calendarService from 'pages/calendar/modules/CalendarService'
import { installSwipe, uninstallSwipe } from 'pages/calendar/utils/Swipe'
import settings from 'popups/Settings'

import syncSVG from 'assets/images/calendar/toolbar-refresh-blue.svg'
import calendarLogoSVG from 'assets/images/logo/calendar-logo-text.svg'
import plusIcon from 'assets/images/settings/setting-plus-24.svg'
import menuIcon from 'assets/images/toolbar-hamberger-24.svg'
import settingsSVG from 'assets/images/toolbar-settings-24.svg'
import searchIcon from 'assets/images/calendar/toolbar-search-24-blue.svg'

type ViewType = 'day' | 'week' | 'month'

interface UserInfo {
  email: string
}

interface PresentData {
  focusedDate: Date
  browseDate: Date
  accountName: string
  user: UserInfo
  isExpanded: boolean
  notiCount: number
}

interface CalendarInstance {
  id: string
  subject?: string
  location?: string
  description?: string
  [key: string]: any
}

interface CalendarMobileProps {
  presentData: PresentData
  getFolderList: () => ReactNode
  onEditEvent: (
    date: Date,
    arg2: null,
    arg3: null,
    arg4: null,
    arg5: boolean,
    arg6: boolean,
    arg7: null,
  ) => void
  onEventClick: (event: any, element: HTMLElement) => void
  onEventDoubleClick: (event: any, element: HTMLElement) => void
  onFolderNotiClick: () => void
  onFolderExpandClick: () => void
  onNewFolder: () => void
  onDateChange: (date: Date) => void
  onNext?: () => void
  onPrev?: () => void
}

const CalendarMobile = ({
  presentData,
  getFolderList,
  onEditEvent,
  onEventClick,
  onEventDoubleClick,
  onFolderNotiClick,
  onFolderExpandClick,
  onNewFolder,
  onDateChange,
  onNext = () => {},
  onPrev = () => {},
}: CalendarMobileProps) => {
  const [viewType, setViewType] = useState<ViewType>('day')
  const [drawerVisible, setDrawerVisible] = useState(false)
  const [datePickerOpen, setDatePickerOpen] = useState(false)
  const [isSyncing, setIsSyncing] = useState(false)
  const [isSearchOpen, setIsSearchOpen] = useState(false)
  const [searchedEvents, setSearchedEvents] = useState<CalendarInstance[] | null>(null)
  const { t } = useTranslation()
  const calendarRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<any>(null)

  const formatDate = (date: Date): string => {
    const locale = settings.getLocale()

    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    })
  }

  const syncTimeExpired = useRef(true)
  const syncListener = (isFetching: boolean) => {
    if (isFetching) {
      setIsSyncing(true)
      syncTimeExpired.current = false

      setTimeout(() => {
        if (!calendarService.isSyncing()) {
          setIsSyncing(false)
        }
        syncTimeExpired.current = true
      }, 1000)
    } else if (syncTimeExpired.current) {
      setIsSyncing(false)
    }
  }

  useEffect(() => {
    if (calendarRef.current) {
      installSwipe(calendarRef.current, onNext, onPrev)
    }
    calendarService.addSyncListener(syncListener)

    return () => {
      if (calendarRef.current) {
        uninstallSwipe(calendarRef.current)
      }
      calendarService.removeSyncListener(syncListener)
    }
  }, [onNext, onPrev])

  useEffect(() => {
    const localCalendar = settings.getValue('localSettings').calendar
    setViewType(localCalendar.calendarViewType)
  }, [])

  const showDrawer = () => {
    setDrawerVisible(true)
  }

  const onClose = () => {
    setDrawerVisible(false)
  }

  const handleViewTypeChange = (newViewType: ViewType) => {
    setViewType(newViewType)
    calendarService.setViewType(newViewType)
    onClose()
  }

  const handleDateSelect = (date: Date) => {
    console.log('handleDateSelect', date)
    setDatePickerOpen(false)
    onDateChange(date)
  }

  const handleDatePickerClose = () => {
    setDatePickerOpen(false)
  }

  const handleSyncClick = () => {
    if (isSyncing) return

    setIsSyncing(true)
    calendarService.reloadEvents(true)
  }

  const handleSearchToggle = () => {
    if (isSearchOpen) {
      setIsSearchOpen(false)
      setSearchedEvents(null)
    } else {
      setIsSearchOpen(true)
    }
  }

  const handleSearchChange = (keyword: string, text: string) => {
    if (text === null || text === '') {
      setSearchedEvents(null)
      return
    }

    const sText = text.toLowerCase()
    setSearchedEvents(
      calendarService.getInstances().filter((instance: CalendarInstance) => {
        return (
          instance.subject?.toLowerCase().includes(sText) ||
          instance.location?.toLowerCase().includes(sText) ||
          instance.description?.toLowerCase().includes(sText)
        )
      }),
    )
  }

  const handleSearchClose = () => {
    setIsSearchOpen(false)
  }

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleSearchClose()
    }
  }

  const onClickSearchEvent = (
    instance: CalendarInstance,
    date: Date,
    inRef: React.RefObject<HTMLElement>,
  ) => {
    const event = {
      instanceId: instance.id,
      date: date,
      instance: instance,
    }
    if (inRef.current) {
      onEventClick(event, inRef.current)
    }
  }

  return (
    <div className={styles.main} ref={calendarRef}>
      {/* 헤더 */}
      <div className={styles.header}>
        <div className={styles.left}>
          <OmButton
            variant="text"
            icon={<img src={menuIcon} alt="menu" style={{ width: 24, height: 24 }} />}
            onClick={showDrawer}
            style={{ marginRight: 8 }}
          />
          <OmPopover
            open={datePickerOpen}
            onOpenChange={(next) => {
              if (!isSearchOpen) setDatePickerOpen(next)
            }}
            placement="bottomLeft"
            arrow={false}
            bodyStyle={{ padding: 0 }}
            content={
              viewType === 'month' ? (
                <CalendarDatePicker
                  pickerType={DatePickerType.MONTH}
                  value={presentData.focusedDate}
                  onChange={handleDateSelect}
                  onClose={handleDatePickerClose}
                />
              ) : (
                <div className={styles.datePickerContainer}>
                  <CalendarDatePicker
                    pickerType={DatePickerType.DATE}
                    value={presentData.focusedDate}
                    disableExpand={true}
                    onChange={handleDateSelect}
                  />
                </div>
              )
            }
          >
            <span className={styles.headerDate}>{formatDate(presentData.focusedDate)}</span>
          </OmPopover>
        </div>
        <div className={styles.right}>
          <OmButton
            variant="text"
            icon={<img src={searchIcon} alt="search" style={{ width: 24, height: 24 }} />}
            onClick={handleSearchToggle}
          />
          <OmButton
            variant="text"
            icon={<img src={syncSVG} alt="sync" />}
            className={`${styles.syncButton} ${isSyncing ? styles.rotating : ''}`}
            onClick={handleSyncClick}
          />
          <OmButton
            variant="text"
            icon={<img src={plusIcon} alt="plus" style={{ width: 24, height: 24 }} />}
            style={{ marginLeft: 'auto', marginRight: 0 }}
            onClick={() => {
              onEditEvent(presentData.focusedDate, null, null, null, false, false, null)
            }}
            disabled={isSearchOpen}
          />
        </div>
      </div>

      {/* 검색 오버레이 */}
      {isSearchOpen && (
        <div className={styles.searchOverlay} onClick={handleOverlayClick}>
          <div className={styles.searchBar} id="mobile-search-container">
            <SearchPortal
              innerRef={(ref: any) => {
                searchInputRef.current = ref
              }}
              presentFocus={true}
              rootId="mobile-search-container"
              hideMenu={true}
              onSearch={handleSearchChange}
              onSearchEnd={handleSearchClose}
              searchDelay={2000}
            />
          </div>
          {searchedEvents && (
            <div className={styles.agendaContainer}>
              <AgendaBox events={searchedEvents as any} onEventClick={onClickSearchEvent} />
            </div>
          )}
        </div>
      )}

      {/* 캘린더 뷰 */}
      <div className={styles.calendarView}>
        {viewType === 'day' && (
          <DayBox
            date={presentData.focusedDate}
            onEditEvent={onEditEvent as any}
            onEventClick={onEventClick}
            onEventDoubleClick={onEventDoubleClick}
          />
        )}
        {viewType === 'week' && (
          <WeekBox
            date={presentData.focusedDate}
            dayLength={3}
            onEditEvent={onEditEvent as any}
            onEventClick={onEventClick}
            onEventDoubleClick={onEventDoubleClick}
          />
        )}
        {viewType === 'month' && (
          <MobileMonthBox
            browseDate={presentData.browseDate}
            focusedDate={presentData.focusedDate}
            onDateChange={handleDateSelect as any}
            onEventClick={onEventClick}
          />
        )}
      </div>

      {/* Drawer */}
      <OmDrawer
        title={null}
        placement="left"
        onClose={onClose}
        open={drawerVisible}
        width={300}
        closeIcon={null}
        bodyStyle={{ padding: 0 }}
      >
        <div className={styles.drawerContainer}>
          <div className={styles.drawerHeader}>
            <img src={calendarLogoSVG} alt="calendar" />
            <div
              className={styles.drawerHeaderSettings}
              onClick={() => {
                settings.open({})
              }}
            >
              <img src={settingsSVG} alt="settings" />
            </div>
          </div>
          <div className={styles.drawerSeparator} />
          {/* 보기 옵션 */}
          <div className={styles.drawerViewOptionContainer}>
            <div
              className={`${styles.drawerViewOption} ${viewType === 'day' ? styles.active : ''}`}
              onClick={() => handleViewTypeChange('day')}
            >
              {t('calendar.frequency.day')}
            </div>
            <div
              className={`${styles.drawerViewOption} ${viewType === 'week' ? styles.active : ''}`}
              onClick={() => handleViewTypeChange('week')}
            >
              {t('calendar.frequency.3days')}
            </div>
            <div
              className={`${styles.drawerViewOption} ${viewType === 'month' ? styles.active : ''}`}
              onClick={() => handleViewTypeChange('month')}
            >
              {t('calendar.frequency.month')}
            </div>
          </div>
          {/* 세퍼레이터 */}
          <div className={styles.drawerSeparator} />
          <AccountInfoView
            name={presentData.accountName}
            email={presentData.user.email}
            expanded={presentData.isExpanded}
            count={presentData.notiCount}
            onExpandClick={onFolderExpandClick}
            onAddClick={() => {
              onNewFolder()
            }}
            onNotiClick={() => {
              onFolderNotiClick()
            }}
          />
          {presentData.isExpanded && getFolderList()}
        </div>
      </OmDrawer>
    </div>
  )
}

export default CalendarMobile
